// application/useCases/commitFundingSourceVersion.js
import {
  canTakeEffectAt,
  createVersionTimeline,
  minEffectiveFrom,
  nextVersionAt,
  supersededBySaveAt,
  versionsOnDate,
} from '../../domain/model/VersionTimeline';
import { EffectiveDateRejected, VersionCollision } from '../ports/FundingSourceRepository.port';

/**
 * How many times a lost version number is recomputed and retried.
 *
 * Three because contention here is two people saving the same funder within a round trip of each
 * other, which resolves on the next attempt. A series that loses three in a row is not one attempt
 * away from succeeding — it means something is writing continuously, and the user is better served by
 * an error naming the funder than by a dialog that hangs retrying.
 */
const MAX_ATTEMPTS = 3;

/**
 * Saves a funding source's configuration as of a date.
 *
 * ONE OPERATION FOR ALL THREE SAVE OPTIONS. "Overwrite existing", "take effect today" and "take
 * effect on a date" differ only in which date the caller passes; there is no mode to branch on. See
 * `canTakeEffectAt`, which is where that reduction is argued and where the collapse between the first
 * two options is handled — with the version in force having started today, they are one outcome.
 *
 * THE TIMELINE IS RE-READ HERE, not taken from the caller, and that is the point of the use case
 * rather than an optimization. The editor loaded a timeline when the dialog opened and validated the
 * user's choice against it, but a colleague can schedule a change while the user types: the floor
 * moves, the next version number moves, and a save computed against the stale copy would either be
 * refused by the rules or quietly claim a position that is no longer free. Both checks below run
 * against what storage says now.
 *
 * A COLLISION IS RETRIED, an effective date is not. Losing the version number is a race with a
 * correct resolution — re-read, recompute, write again — and the retry is bounded because a series
 * under continuous contention is a human problem, not one more attempt away. A rejected date is a
 * decision that has to reach the user, since the only honest response is to show them what the
 * timeline now looks like.
 *
 * @param {{ fundingSourceId: string, clinicId: string, asOf: string, effectiveFrom: string,
 *   record: Object }} request - `asOf` is 'YYYY-MM-DD' and comes from the edge; nothing here reads a
 *   clock, so the same input always produces the same decision. `record` is the whole editable
 *   funder, because the whole record versions together.
 * @param {import('../ports/FundingSourceRepository.port').FundingSourceRepository} repository
 * @returns {Promise<string>} the new version's id
 * @throws {EffectiveDateRejected} the date is not one a new version may take
 * @throws {VersionCollision} the version number was taken on every attempt
 */
// eslint-disable-next-line import/prefer-default-export
export async function commitFundingSourceVersion({
  fundingSourceId,
  clinicId,
  asOf,
  effectiveFrom,
  record,
}, repository) {
  let lastCollision = null;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    // Sequential by nature: each attempt has to see the result of the one before it.
    // eslint-disable-next-line no-await-in-loop
    const timeline = createVersionTimeline(await repository.loadVersions({
      fundingSourceId,
      clinicId,
    }));

    if (!canTakeEffectAt(timeline, asOf, effectiveFrom)) {
      throw new EffectiveDateRejected(effectiveFrom, { earliest: minEffectiveFrom(timeline, asOf) });
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      return await repository.appendVersion({
        fundingSourceId,
        clinicId,
        effectiveFrom,
        version: nextVersionAt(timeline, effectiveFrom),
        record,
        // Entries already on this date lose the read flag, not their row: an earlier entry for the
        // same date is still true history, it is simply no longer the one to read.
        supersededVersionIds: versionsOnDate(timeline, effectiveFrom)
          .map((superseded) => superseded.versionId),
        // A change scheduled for a DIFFERENT date is canceled outright — the decision the editor
        // warns about before confirming.
        cancelledVersionIds: supersededBySaveAt(timeline, asOf, effectiveFrom)
          .map((cancelled) => cancelled.versionId),
      });
    } catch (error) {
      // Anything that is not a lost race is the caller's to handle immediately.
      if (!(error instanceof VersionCollision)) {
        throw error;
      }
      lastCollision = error;
    }
  }

  // Out of attempts. The last collision is rethrown rather than a summary of the loop, so the
  // coordinates that were actually contended survive to whatever reports this.
  throw lastCollision;
} // commitFundingSourceVersion()
